/**
 * Onboarding step 6 — Goals & KPIs. Up to three goals, each with a
 * metric and a target; persisted as one `goals` Brain fact on activation.
 */

import { Plus, Trash2 } from 'lucide-react';
import type { DraftGoal, OnboardingDraft } from '../../../lib/onboarding';

const MAX_GOALS = 3;

interface Props {
  draft: OnboardingDraft;
  onPatch: (patch: Partial<OnboardingDraft>) => void;
}

export function StepGoals({ draft, onPatch }: Props) {
  const setRow = (idx: number, patch: Partial<DraftGoal>) =>
    onPatch({
      goals: draft.goals.map((g, i) => (i === idx ? { ...g, ...patch } : g)),
    });

  const addRow = () =>
    onPatch({ goals: [...draft.goals, { goal: '', metric: '', target: '' }] });

  const removeRow = (idx: number) =>
    onPatch({ goals: draft.goals.filter((_, i) => i !== idx) });

  return (
    <div className="onb-step">
      <h3 className="t-section-title onb-step__title">Goals &amp; KPIs</h3>
      <p className="t-meta onb-step__blurb">
        What are you driving toward? Argo ties agent work back to these.
      </p>

      {draft.goals.map((g, idx) => (
        <div key={idx} className="onb-row onb-repeat-row">
          <div style={{ flex: 2 }}>
            <label className="t-eyebrow onb-label" htmlFor={`onb-goal-${idx}`}>
              Goal {idx + 1}
            </label>
            <input
              id={`onb-goal-${idx}`}
              className="glass-input"
              value={g.goal}
              onChange={(e) => setRow(idx, { goal: e.target.value })}
              placeholder="Hit $1M ARR"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="t-eyebrow onb-label" htmlFor={`onb-goal-metric-${idx}`}>
              Metric
            </label>
            <input
              id={`onb-goal-metric-${idx}`}
              className="glass-input"
              value={g.metric}
              onChange={(e) => setRow(idx, { metric: e.target.value })}
              placeholder="ARR"
            />
          </div>
          <div style={{ flex: 1 }}>
            <label className="t-eyebrow onb-label" htmlFor={`onb-goal-target-${idx}`}>
              Target
            </label>
            <input
              id={`onb-goal-target-${idx}`}
              className="glass-input"
              value={g.target}
              onChange={(e) => setRow(idx, { target: e.target.value })}
              placeholder="$1,000,000"
            />
          </div>
          {draft.goals.length > 1 && (
            <button
              type="button"
              className="glass-btn onb-remove"
              onClick={() => removeRow(idx)}
              aria-label={`Remove goal ${idx + 1}`}
            >
              <Trash2 size={13} />
            </button>
          )}
        </div>
      ))}

      {draft.goals.length < MAX_GOALS && (
        <button type="button" className="glass-btn" onClick={addRow}>
          <Plus size={13} /> Add goal
        </button>
      )}
    </div>
  );
}
